//  eslint-disable import/no-extraneous-dependencies
import {format} from 'date-fns';
import {useState, useEffect, useCallback} from 'react';

import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import {Box, Typography} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {randomId} from '@mui/x-data-grid-generator';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  DataGridPremium,
  GridAggregationFunction,
  GRID_AGGREGATION_FUNCTIONS
} from '@mui/x-data-grid-premium';
import {
  MuiEvent,
  GridRowId,
  GridRowsProp,
  GridRowModes,
  GridRowModel,
  GridCellParams,
  GridRowModesModel,
  GridEventListener,
  GridToolbarExport,
  GridActionsCellItem,
  GridToolbarContainer,
  GridRowEditStopReasons,
  GridToolbarQuickFilter,
  GridColumnHeaderParams,
  GridToolbarFilterButton,
  GridRowEditStartReasons,
  GridToolbarColumnsButton,
  GridColumnVisibilityModel,
  GridToolbarDensitySelector
} from '@mui/x-data-grid';

import EmptyContent from 'src/components/empty-content/empty-content';

import './styles.css';

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  column_data: any;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const getFormattedValue = (value: any, value_type: any) => {
  switch (value_type) {
    case 'currency':
      return currencyFormatter.format(value).replace(/\.00$/, '');
    case 'rating':
      return value?.length ? value : '0';
    case 'date':
      return value?.length && new Date(value) ? value : new Date();
    default:
      return value ?? '';
  }
};

function EditToolbar(props: EditToolbarProps) {
  const {setRows, setRowModesModel, column_data} = props;

  const handleClick = () => {
    const id = randomId();
    let firstField: any = null;
    setRows(oldRows => {
      const temp_obj: any = {};
      Object.entries(oldRows?.[0]).forEach(([key, value]) => {
        if (!firstField?.length && key !== 'id') {
          firstField = key;
        }
        temp_obj[key] = getFormattedValue('', column_data?.type?.[key]);
      });
      return [{...temp_obj, id, isNew: true}, ...oldRows];
    });
    setRowModesModel(oldModel => ({
      ...oldModel,
      [id]: {mode: GridRowModes.Edit, fieldToFocus: firstField}
    }));
  };

  return (
    <GridToolbarContainer sx={{display: 'flex'}}>
      <>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add record
        </Button>
      </>
      <>
        <GridToolbarQuickFilter />
        <Box sx={{flexGrow: 1}} />
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </>
    </GridToolbarContainer>
  );
}

const HIDE_COLUMNS = {
  id: false
};

const HIDE_COLUMNS_TOGGLABLE = ['id', 'actions'];

export default function FullFeaturedCrudGrid(props: any) {
  const {data, column_data} = props;
  const [rows, setRows] = useState<any>([]);
  const [mainColumns, setMainColumns] = useState<any>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(HIDE_COLUMNS);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  const handleRowEditStart: GridEventListener<'rowEditStart'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStartReasons.enterKeyDown) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    },
    [rowModesModel]
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    },
    [rowModesModel]
  );

  const handleDeleteClick = useCallback(
    (id: GridRowId) => () => {
      setRows(rows.filter((row: any) => row.id !== id));
    },
    [rows]
  );

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: {mode: GridRowModes.View, ignoreModifications: true}
      });

      const editedRow: any = rows.find((row: any) => row.id === id);
      if (editedRow!.isNew) {
        setRows(rows.filter((row: any) => row.id !== id));
      }
    },
    [rowModesModel, rows]
  );

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow: any = {...newRow, isNew: false};
    setRows(rows?.map((row: any) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const getTogglableColumns = () =>
    mainColumns
      .filter((column: any) => !HIDE_COLUMNS_TOGGLABLE.includes(column.field))
      .map((column: any) => column.field);

  useEffect(() => {
    if (data?.length) {
      setRows(data);
    }
  }, [data]);

  const getTypeValue = (value_type: any) => {
    switch (value_type) {
      case 'currency':
        return 'number';
      default:
        return value_type ?? 'text';
    }
  };

  useEffect(() => {
    const generateColumns = (rows_data: any) => {
      const columnList: any = [];
      const [data_obj] = rows_data;
      Object.entries(data_obj).forEach(([key, value]) => {
        const obj: any = {
          field: key,
          headerName: key,
          editable: !column_data?.disableEdit?.[key],
          width: column_data?.columnWidth?.[key] ?? 'auto',
          align: 'left',
          headerAlign: 'left',
          flex: column_data?.columnWidth?.[key] ? 'auto' : 1,
          type: getTypeValue(column_data?.type?.[key]),
          valueOptions: column_data?.options?.[key],
          valueFormatter: (ele_obj: any) =>
            getFormattedValue(ele_obj?.value, column_data?.type?.[key]),
          // renderCell: (ele: any) => renderDataGridCell(ele, key, column_data?.type?.[key], column_data?.options?.[key])
          renderHeader: (params: GridColumnHeaderParams) => (
            <Typography variant="h6">{`${key}`}</Typography>
          )
        };
        if (column_data?.type?.[key] === 'date') {
          obj.renderCell = (ele: any) => (
            <span>{format(new Date(ele.value ?? null), 'MM/dd/yyyy')}</span>
          );
        }
        if (column_data?.renderCell?.[key]) {
          obj.renderCell = column_data?.renderCell?.[key];
          delete obj.valueFormatter;
        }
        if (column_data?.renderEditCell?.[key]) {
          obj.renderEditCell = column_data?.renderEditCell?.[key];
        }
        if (column_data?.type?.[key] === 'currency') {
          obj.align = 'right';
          obj.headerAlign = 'right';
          // obj.headerClassName = 'right-aligned-cell'; // You can define this class in your CSS
          // obj.cellClassName = 'right-aligned-cell'; // You can define this class in your CSS
        }

        columnList.push(obj);
      });
      const hasEditRows = columnList.find((item: any) => item.editable);
      if (hasEditRows) {
        columnList.push({
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          renderHeader: (params: GridColumnHeaderParams) => (
            <Typography variant="h6">Actions</Typography>
          ),
          getActions: ({id}: any) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  key={data_obj?.id + '_save'}
                  icon={<SaveIcon />}
                  label="Save"
                  sx={{
                    color: 'primary.main'
                  }}
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  key={data_obj?.id + '_cancel'}
                  icon={<CancelIcon />}
                  label="Cancel"
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />
              ];
            }

            return [
              <GridActionsCellItem
                key={data_obj?.id + '_edit'}
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                key={data_obj?.id + '_delete'}
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDeleteClick(id)}
                color="inherit"
              />
            ];
          }
        });
      }

      return columnList;
    };
    if (rows?.length) {
      setMainColumns(generateColumns(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    column_data,
    data,
    handleCancelClick,
    handleDeleteClick,
    handleEditClick,
    handleSaveClick,
    rowModesModel,
    rows
  ]);

  const display_title: GridAggregationFunction<
    {gross: number; budget: number},
    string
  > = {
    label: '',
    getCellValue: ({row}) => ({budget: row.budget, gross: row.gross}),
    apply: ({values}) => 'Total',
    columnTypes: ['text']
  };

  return (
    <Box
      sx={{
        // height: props?.gridHeight ?? '70vh',
        width: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        }
      }}>
      {rows?.length && mainColumns?.length ? (
        <DataGridPremium
          sx={{
            '.MuiDataGrid-aggregationColumnHeaderLabel': {
              display: 'none'
            }
          }}
          density="compact"
          rows={rows}
          columns={mainColumns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStart={handleRowEditStart}
          onRowEditStop={handleRowEditStop}
          onCellClick={(
            params: GridCellParams,
            event: MuiEvent<React.MouseEvent>
          ) => {
            event.defaultMuiPrevented = true;
            if (props?.onCellClick) {
              props?.onCellClick(params, event);
            }
          }}
          processRowUpdate={processRowUpdate}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel =>
            setColumnVisibilityModel(newModel)
          }
          slots={{
            toolbar: params => (
              <EditToolbar {...params} column_data={column_data} />
            ),
            noRowsOverlay: () => <EmptyContent title="No Data" />,
            noResultsOverlay: () => <EmptyContent title="No results found" />
          }}
          slotProps={{
            toolbar: {setRows, setRowModesModel, showQuickFilter: true},
            columnsPanel: {
              getTogglableColumns
            }
          }}
          initialState={props?.initialState}
          aggregationFunctions={{
            ...GRID_AGGREGATION_FUNCTIONS,
            display_title
          }}
        />
      ) : null}
    </Box>
  );
}
